import { Icon } from "./Icon";

/* The stage path on lead and opportunity pages (UX-146): the open stages as arrow segments,
   earlier ones done (with a check), the current one highlighted, later ones muted, and the
   outcomes (Closed Won / Closed Lost, Qualified / Disqualified) as a final segment coloured won
   or lost. Clicking a segment does what the page decides (move, or open a dialog). Phones get
   "Stage 3 of 5: Proposal" above a path that scrolls sideways. */

export interface PathStep {
  key: string;
  label: string;
}

export interface PathOutcome extends PathStep {
  /** "hold" (amber): an open on-hold state off the path, such as a bug's Need Information. */
  tone: "won" | "lost" | "hold";
}

type State = "done" | "current" | "todo";

export function stepState(steps: PathStep[], outcomes: PathOutcome[], current: string, key: string): State {
  if (key === current) return "current";
  const at = steps.findIndex((s) => s.key === current);
  const index = steps.findIndex((s) => s.key === key);
  if (index < 0) return "todo"; // an outcome (or on-hold state) that isn't the current one
  if (at >= 0) return index < at ? "done" : "todo";
  // Finished: a win completed every stage; a loss leaves them muted. On hold (a "hold" outcome,
  // such as a bug's Need Information) is open but off the path, so the stages stay upcoming.
  return outcomes.find((o) => o.key === current)?.tone === "won" ? "done" : "todo";
}

const SAYS: Record<State, string> = { done: "completed", current: "current stage", todo: "upcoming" };

export function StagePath({
  label,
  steps,
  outcomes,
  current,
  onSelect,
  disabled,
  readOnly,
}: {
  /** Names the path for screen readers, e.g. "Opportunity stage". */
  label: string;
  steps: PathStep[];
  outcomes: PathOutcome[];
  current: string;
  onSelect?: (key: string) => void;
  /** Whether a segment can't be chosen now. */
  disabled?: (key: string) => boolean;
  /** Show the status only (the client portal): no segment can be chosen. */
  readOnly?: boolean;
}) {
  const off = (key: string) => !!readOnly || !!disabled?.(key);
  const at = steps.findIndex((s) => s.key === current);
  const outcome = outcomes.find((o) => o.key === current);
  const summary =
    at >= 0
      ? `Stage ${at + 1} of ${steps.length}: ${steps[at]!.label}`
      : outcome
        ? `${outcome.tone === "hold" ? "On hold" : "Outcome"}: ${outcome.label}`
        : "";
  return (
    <nav className={readOnly ? "spath spath-ro" : "spath"} aria-label={label}>
      {summary && (
        <p className="spath-summary" aria-hidden="true">
          {summary}
        </p>
      )}
      <ol className="spath-list">
        {steps.map((step) => {
          const state = stepState(steps, outcomes, current, step.key);
          return (
            <li key={step.key} className={`spath-seg ${state}`}>
              <button
                type="button"
                aria-label={`${step.label}, ${SAYS[state]}`}
                aria-current={state === "current" ? "step" : undefined}
                disabled={off(step.key)}
                onClick={() => step.key !== current && onSelect?.(step.key)}
              >
                {state === "done" && <Icon name="check" />}
                <span>{step.label}</span>
              </button>
            </li>
          );
        })}
        <li className="spath-end" aria-hidden="true" />
        {outcomes.map((o) => {
          const here = o.key === current;
          return (
            <li key={o.key} className={`spath-out ${o.tone}${here ? " current" : ""}`}>
              <button
                type="button"
                aria-label={`${o.label}, ${o.tone === "hold" ? "on hold" : here ? "current stage" : "final outcome"}${o.tone === "hold" && here ? ", current stage" : ""}`}
                aria-current={here ? "step" : undefined}
                disabled={off(o.key)}
                onClick={() => !here && onSelect?.(o.key)}
              >
                {here && <Icon name={o.tone === "won" ? "check" : o.tone === "hold" ? "clock" : "x"} />}
                <span>{o.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
